import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';

import { SiteBrand, SiteNav } from '@/components/Layout';
import {
  useCreateMachineShare,
  useGetXpraSession,
  useGetXpraSessionDeadline,
} from '@/lib/api/orval/target/machines/machines';
import type { Machine } from '@/lib/types';
import { buildConfig } from '@/config';
import { disconnectTarget, xpraNonce } from '@/lib/xpra';

interface XpraLocationState {
  wssUri?: string;
  machine?: Machine;
  redirectUrl?: string;
}

// The shutdown deadline is estimated from the webapp's session row rather than
// the host's claim time, so one this close to the deletion deadline is taken to
// be the same event, and deletion is the one worth naming.
const SAME_DEADLINE_SLOP_SECONDS = 5 * 60;

// How long to wait before asking for a place again, after being turned away.
const RETRY_DELAY_MS = 8000;

function formatRemaining(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const hms = [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
  return d > 0 ? `${d}d ${hms}` : hms;
}

function Countdown({ nonce }: { nonce: string }): React.ReactElement | null {
  const { data } = useGetXpraSessionDeadline(nonce, {
    query: { refetchInterval: 5 * 60 * 1000 },
  });
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!data) return null;
  const shutdownAt = data.shutdown_at ?? 0;
  const retainUntil = data.retain_until;
  let deadline: { at: number; verb: string } | null = null;
  if (
    retainUntil > 0 &&
    (shutdownAt <= 0 || retainUntil <= shutdownAt + SAME_DEADLINE_SLOP_SECONDS)
  ) {
    deadline = { at: retainUntil, verb: 'Deleted' };
  } else if (shutdownAt > 0) {
    deadline = { at: shutdownAt, verb: 'Shuts down' };
  }
  if (!deadline) return null;

  const remaining = deadline.at - now;
  return (
    <span
      className="text-sm tabular-nums"
      style={{ color: remaining < 15 * 60 ? 'var(--danger)' : 'var(--text-secondary)' }}
      title={new Date(deadline.at * 1000).toLocaleString()}
    >
      {remaining > 0
        ? `${deadline.verb} in ${formatRemaining(remaining)}`
        : `${deadline.verb} now`}
    </span>
  );
}

async function copyToClipboard(path: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(window.location.origin + path);
    return true;
  } catch {
    return false; // clipboard unavailable; the link's href still works
  }
}

function ShareSessionButton({ publicId }: { publicId: string }): React.ReactElement {
  const create = useCreateMachineShare();
  const [path, setPath] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<'full' | 'readonly'>('readonly');

  async function mint(): Promise<void> {
    const share = await create.mutateAsync({ publicId, data: { mode } });
    setPath(share.share_path);
    if (await copyToClipboard(share.share_path)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <span
      className="text-sm whitespace-nowrap flex items-center gap-2"
      title="Anonymously created machines are limited to one share link and two connecting clients."
    >
      <select
        aria-label="Share permissions"
        value={mode}
        onChange={(event) => { setMode(event.target.value as 'full' | 'readonly'); setPath(null); }}
        className="themed-input text-sm"
      >
        <option value="readonly">View only</option>
        <option value="full">Allow control</option>
      </select>
      {path && <a href={path}>{copied ? 'Link copied' : 'Share link'}</a>}
      <button
        type="button"
        onClick={mint}
        disabled={create.isPending}
        className="p-0 border-0 bg-transparent cursor-pointer"
        style={{ color: 'var(--accent)' }}
      >
        {path ? 'New link' : 'Share session'}
      </button>
    </span>
  );
}

/**
 * Hosts the live xpra HTML5 desktop session at /xpra/<nonce>.
 *
 * Navigations from a fork/launch carry the `wss://<host>/xpra-<nonce>/` URI in
 * router state; a bare /xpra/<nonce> (a reload, or a guest's share link) is
 * resolved through the API instead. We embed the full xpra HTML5 client (served
 * statically at `/static/xpra-client.html`) in an iframe, passing the session
 * URI + redirect target as query params — the same contract the Django template
 * used. The client `postMessage`s us (the parent) as it goes: `task-loaded`
 * once its page is up, `xpra-connected` / `xpra-connection-lost` as its session
 * comes and goes, and `xpra-disconnected` when it is finished with us.
 */
export function XpraClient(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const state = (location.state as XpraLocationState) || {};
  const nonce = params.nonce ?? (state.wssUri ? xpraNonce(state.wssUri) : undefined);
  const [loaded, setLoaded] = useState(false);
  // Whether the embedded client actually has a session, as opposed to having
  // merely loaded: one the proxy turned away sits there retrying.
  const [connected, setConnected] = useState(false);
  // A client the proxy turned away reports a close like any other. Leaving the
  // page on that would drop the visitor back where they came from before they
  // could read why, so we only leave a session we actually had, and otherwise
  // reload the client to try again for a place.
  const hasConnected = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const retryTimer = useRef<number | null>(null);
  const { data: session, isLoading } = useGetXpraSession(nonce ?? '', {
    // The proxy turns a connection away with a 429 the browser cannot show us,
    // so how full the session is is the only way to explain the wait. Keep
    // asking until the desktop is up and there is a place for it.
    query: {
      enabled: Boolean(nonce),
      retry: false,
      refetchInterval: connected ? false : 5000,
    },
  });
  const wssUri = session?.wss_uri ?? state.wssUri;
  const machineName = session?.machine_name ?? state.machine?.name;
  const machinePublicId = session?.machine_public_id ?? state.machine?.public_id;
  // What the iframe was told at load; changing it would reload the session.
  const redirectUrl = state.redirectUrl || '/';
  // Where we send someone leaving the session, which can improve once the
  // machine is known.
  const leaveTo = state.redirectUrl || (machinePublicId ? `/m/${machinePublicId}` : '/');
  // Reported in the bar rather than over the desktop, and for as long as we
  // are shut out: the embedded client paints its own reconnection progress
  // over the desktop, which would bury it within a moment.
  const isFull =
    typeof session?.connlimit === 'number' &&
    session.connlimit > 0 &&
    (session.connections_open ?? 0) >= session.connlimit;

  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      // Only trust messages from our own origin (the same-origin iframe).
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'task-loaded') {
        setLoaded(true);
      } else if (data.type === 'xpra-connected') {
        hasConnected.current = true;
        setConnected(true);
      } else if (data.type === 'xpra-connection-lost') {
        setConnected(false);
      } else if (data.type === 'xpra-disconnected') {
        if (!hasConnected.current) {
          if (retryTimer.current === null) {
            retryTimer.current = window.setTimeout(() => {
              retryTimer.current = null;
              setAttempt((n) => n + 1);
            }, RETRY_DELAY_MS);
          }
          return;
        }
        const target = disconnectTarget(data, window.location.pathname, leaveTo);
        if (target) navigate(target);
      }
    }
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      if (retryTimer.current !== null) {
        window.clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
  }, [navigate, leaveTo]);

  const bar = (
    <header
      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 px-4 py-1.5 border-b"
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="flex items-center gap-4 min-w-0">
        <SiteBrand className="text-lg whitespace-nowrap" />
        {wssUri && (
          <>
            <h1 className="font-semibold truncate">
              {machinePublicId ? (
                <Link to={`/m/${machinePublicId}`} style={{ color: 'var(--text-primary)' }}>
                  {machineName ?? 'Your desktop'}
                </Link>
              ) : (
                (machineName ?? 'Your desktop')
              )}
            </h1>
            {session?.mode === 'readonly' && (
              <span className="text-sm whitespace-nowrap">View only</span>
            )}
            {nonce && <Countdown nonce={nonce} />}
            {isFull && !connected && (
              <span className="text-sm whitespace-nowrap" style={{ color: 'var(--danger)' }}>
                Session full: {session?.connections_open} of {session?.connlimit} connected
              </span>
            )}
            {session?.can_share && machinePublicId && (
              <ShareSessionButton publicId={machinePublicId} />
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-4">
        {wssUri && (
          <Link to={leaveTo} className="btn-secondary py-1 text-sm">
            Leave session
          </Link>
        )}
        <SiteNav compact />
      </div>
    </header>
  );

  if (!wssUri) {
    return (
      <>
        {bar}
        <div className="max-w-5xl mx-auto px-6 py-8">
          {nonce && isLoading ? (
            <p className="text-center" style={{ color: 'var(--text-secondary)' }}>
              Opening session…
            </p>
          ) : (
            <div className="themed-card p-6 text-center flex flex-col gap-3">
              <p>No active session. Launch or fork a machine to open its desktop.</p>
              <Link to="/" className="btn-primary self-center">
                Back home
              </Link>
            </div>
          )}
        </div>
      </>
    );
  }

  // The client page is not content-hashed, and is served with a long max-age
  // by the node static mount and no cache headers at all by the httpd in front
  // of it, so a deploy only reaches a browser that asks for a new URL.
  const iframeSrc =
    '/static/xpra-client.html?xpra_session_uri=' +
    encodeURIComponent(wssUri) +
    '&redirect_url=' +
    encodeURIComponent(redirectUrl) +
    '&readonly=' + (session?.mode === 'readonly' ? 'true' : 'false') +
    '&v=' +
    encodeURIComponent(buildConfig.assetVersion);

  return (
    <div className="h-full grid grid-rows-[auto_minmax(0,1fr)]">
      {bar}
      <div className="relative">
        {!loaded && (
          <div
            className={
              'absolute inset-0 z-10 px-6 text-center ' +
              'flex flex-col gap-2 items-center justify-center'
            }
            style={{ color: 'var(--text-secondary)' }}
          >
            <p>{isFull ? 'Waiting for a free connection…' : 'Connecting to your desktop…'}</p>
          </div>
        )}
        <iframe
          key={attempt}
          title="xpra session"
          src={iframeSrc}
          className="absolute inset-0 w-full h-full border-0"
          style={{ background: '#000' }}
          allow="clipboard-read; clipboard-write; fullscreen"
        />
      </div>
    </div>
  );
}
